import type { BadgeProps } from '@commenda/kakashi';

import {
  IServiceAuditorFinding,
  ServiceAuditorFindingCategory,
  ServiceAuditorFindingSeverity,
} from '@commenda/api/agent-platform/service-auditor/types';

export interface IServiceAuditBadgeConfig {
  label: string;
  variant: BadgeProps['variant'];
}

export class ServiceAuditFindingBadgeHelper {
  static readonly INTAKE_GAP_BADGE: IServiceAuditBadgeConfig = {
    label: 'Intake gap',
    variant: 'blue',
  };

  static readonly PENALTY_RISK_BADGE: IServiceAuditBadgeConfig = {
    label: 'Penalty risk',
    variant: 'error',
  };

  static readonly SEVERITY_BADGES: Readonly<
    Record<ServiceAuditorFindingSeverity, IServiceAuditBadgeConfig>
  > = {
    [ServiceAuditorFindingSeverity.HIGH]: {
      label: 'High severity',
      variant: 'error',
    },
    [ServiceAuditorFindingSeverity.MEDIUM]: {
      label: 'Medium severity',
      variant: 'warning',
    },
    [ServiceAuditorFindingSeverity.LOW]: {
      label: 'Low severity',
      variant: 'gray',
    },
  };

  static getFindingBadges(
    finding: Readonly<IServiceAuditorFinding>
  ): IServiceAuditBadgeConfig[] {
    const categoryBadge =
      ServiceAuditFindingBadgeHelper.getCategoryBadge(finding);
    const severityBadge =
      ServiceAuditFindingBadgeHelper.getSeverityBadge(finding);
    return [
      ...(categoryBadge ? [categoryBadge] : []),
      ...(severityBadge ? [severityBadge] : []),
    ];
  }

  private static getCategoryBadge(
    finding: Readonly<IServiceAuditorFinding>
  ): IServiceAuditBadgeConfig | null {
    if (finding.category === ServiceAuditorFindingCategory.INTAKE_GAP) {
      return ServiceAuditFindingBadgeHelper.INTAKE_GAP_BADGE;
    }
    return finding.category === ServiceAuditorFindingCategory.PENALTY_RISK
      ? ServiceAuditFindingBadgeHelper.PENALTY_RISK_BADGE
      : null;
  }

  // Intake gaps are about the workflow, so only findings about the draft carry a severity.
  private static getSeverityBadge(
    finding: Readonly<IServiceAuditorFinding>
  ): IServiceAuditBadgeConfig | null {
    if (finding.category === ServiceAuditorFindingCategory.INTAKE_GAP) {
      return null;
    }
    return ServiceAuditFindingBadgeHelper.SEVERITY_BADGES[finding.severity];
  }
}
